import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowUp, Loader2 } from "lucide-react";
import { isNull } from "lodash";

interface QueryBarProps {
  onSubmit: (query: string) => void;
  isLoading: boolean;
  className?: string;
  outerClassName?: string;
  suggestions?: string[];
  id?: string | null;
  placeholder?: string;
  supressDisclaimer?: boolean;
  embedded?: boolean;
}

const QueryBar: React.FC<QueryBarProps> = ({
  onSubmit,
  isLoading,
  className,
  outerClassName,
  id,
  placeholder,
  supressDisclaimer = false,
  embedded = false,
}) => {
  const [userQuery, setUserQuery] = useState("");
  const [, setShowAbove] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (embedded) {
      return;
    }

    if (inputRef.current && isNull(id)) {
      inputRef.current.focus();
    }
    return () => {};
  }, [id, embedded]);

  useEffect(() => {
    if (formRef.current) {
      const rect = formRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setShowAbove(spaceBelow < 200); // Show above if less than 200px space below
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userQuery.trim() === "") {
      return;
    }
    onSubmit(userQuery);
    setUserQuery("");
  };

  return (
    <div className={`relative max-w-3xl mx-auto ${outerClassName || ""}`}>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={`w-full flex items-center bg-white dark:bg-gray-800 rounded-full overflow-hidden h-14 p-[2px] ${
          className || ""
        } bg-orange-gradient border-1
        shadow-orange-glow
        hover:shadow-orange-glow-hover
        focus-within:shadow-orange-glow-hover
        transition-all duration-300`}
      >
        <div
          id="query-input"
          className="flex items-center w-full h-full bg-white dark:bg-gray-800 rounded-full"
        >
          <Input
            ref={inputRef}
            className="flex-grow text-lg px-6 h-full flex items-center border-none focus:ring-0 bg-transparent"
            placeholder={placeholder || "What can I help you with today?"}
            value={userQuery}
            onChange={(e) => setUserQuery(e.target.value)}
          />
          <Button
            type="submit"
            className="rounded-full p-2 aspect-square h-10 w-10 mr-2"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <ArrowUp className="h-5 w-5" />
            )}
          </Button>
        </div>
      </form>

      {!supressDisclaimer && (
        <div className="pb-2 pt-4 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-gray-100 from-50% via-gray-100/90 via-75% to-transparent dark:from-black dark:from-50% dark:via-black/90 dark:via-75% dark:to-transparent" />
          <div className="text-xs text-gray-500 text-center relative">
            <p>
              Your chats aren't used to train our models. Generative AI,
              including PeopleScape.AI's Assistant, can make mistakes.
            </p>
          </div>
        </div>
      )}

      {/* {suggestions.length > 0 && userQuery.length === 0 && (
        <Suggestions
          suggestions={suggestions}
          onSelect={(suggestion) => {
            onSubmit(suggestion);
            setUserQuery("");
          }}
        />
      )} */}
    </div>
  );
};

export default QueryBar;
